<template>
  <div v-if="content" class="ai-platform-page" :class="{ 'hero-style-home': hasHomeHero }">
    <div class="ai-hero-banner">
      <img :src="heroBannerSrc" alt="" style="width: 100%" @load="loaded = true" :class="{ show: loaded }" class="top_img" />
      <div class="ai-hero-title">{{ content.title }}</div>
    </div>

    <nav class="product-tabs-bar">
      <a v-for="tab in tabs" :key="tab.id" class="product-tab" :class="{ active: activeTab === tab.id }" :href="`#${tab.id}`" @click.prevent="scrollTo(tab.id)">{{ tab.label }}</a>
    </nav>

    <div class="product-body">
      <section id="background" class="product-section section-row ai-platform-block" :class="{ 'animate-in': visible.background }">
        <div class="section-title">需求背景</div>
        <div class="section-content">
          <div class="background-card">
            <p class="case-text case-text-full">{{ content.background }}</p>
          </div>
        </div>
      </section>

      <section id="challenges" class="product-section section-row ai-platform-block" :class="{ 'animate-in': visible.challenges }" style="background-color: #fff">
        <div class="section-title">业务挑战</div>
        <div class="section-content">
          <p v-if="content.challengesDescription" class="challenges-description">
            {{ content.challengesDescription }}
          </p>
          <div class="grid-2">
            <div v-for="(item, idx) in content.challenges" :key="idx" class="card">
              <img class="card-icon" :src="`/src/assets/jiejue/${(idx % 4) + 1}.svg`" alt="icon" />
              <div class="card-text">
                <div class="card-title">{{ cleanListPrefix(item.title) }}</div>
                <div class="card-desc">{{ cleanListPrefix(item.desc) }}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="scenarios" class="product-section section-row ai-platform-block" :class="{ 'animate-in': visible.scenarios }">
        <div class="section-title">场景应用</div>
        <div class="section-content">
          <div class="scenarios">
            <div class="scenario-text">
              <div v-for="(item, idx) in content.scenarios" :key="idx" class="scenario-item">
                <div class="item-title">{{ item.title }}</div>
                <div class="item-desc">{{ item.desc }}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section v-if="SHOW_SOLUTION_DEPLOY_DIAGRAM" id="architecture" class="product-section section-row ai-platform-block" :class="{ 'animate-in': visible.architecture }" style="background-color: #fff">
        <div class="section-title">部署示意图</div>
        <div class="section-content">
          <img :src="architectureSrc" alt="部署示意图" class="architecture-image" />
        </div>
      </section>

      <section id="advantages" class="product-section section-row advantages ai-platform-block" :class="{ 'animate-in': visible.advantages }">
        <div class="section-title">方案优势</div>
        <div class="section-content">
          <div class="grid-4">
            <div v-for="(item, idx) in content.advantages" :key="idx" class="adv-card">
              <img :src="`/src/assets/jiejue/${(idx % 4) + 1}.svg`" alt="" class="adv-badge" />
              <div class="adv-title">{{ cleanListPrefix(item.title) }}</div>
              <div class="adv-desc">{{ cleanListPrefix(item.desc) }}</div>
            </div>
          </div>
        </div>
      </section>

      <section id="cases" class="product-section section-row ai-platform-block" :class="{ 'animate-in': visible.cases }">
        <div class="section-title">典型案例</div>
        <div class="section-content section-content-cases">
          <div class="case-content case-content-blink">
            <div class="case-section">
              <h3 class="case-subtitle">需求背景</h3>
              <p class="case-text">{{ content.caseBackground }}</p>
            </div>
            <div class="case-section">
              <h3 class="case-subtitle">用户痛点</h3>
              <ul class="case-list">
                <li v-for="(item, idx) in content.caseDifficulties" :key="idx">{{ item }}</li>
              </ul>
            </div>
            <div class="case-section">
              <h3 class="case-subtitle">解决方案</h3>
              <ul class="case-list">
                <li v-for="(item, idx) in content.caseSolutions" :key="idx">{{ item }}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>

    
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { SHOW_SOLUTION_DEPLOY_DIAGRAM } from '@/data/solutionUi.js'
import { edgeInfrastructureSolutions } from '../../data/edgeInfrastructureSolutions'

import aiArch from '@/assets/solutions/deploy/ai-infrastructure.svg'
import serverArch from '@/assets/solutions/deploy/server-virtualization.svg'
import edgeArch from '@/assets/solutions/deploy/edge-computing.svg'
import fusionArch from '@/assets/solutions/deploy/hci.svg'
import dbArch from '@/assets/solutions/deploy/paas.svg'
import netArch from '@/assets/solutions/deploy/compute-network.svg'
import defaultHeroBanner from '@/assets/jiejue/custom/resource-pool-hero-no-watermark.png'
import serverVirtualizationHeroBanner from '@/assets/solutions/edge-infra/server-virtualization-hero.png'
import aiInfrastructureHeroBanner from '@/assets/jiejue/custom/ai-infrastructure-hero-banner.svg'
import edgeComputingHeroBanner from '@/assets/jiejue/custom/edge-computing-hero-banner.svg'
import hciSolutionHeroBanner from '@/assets/jiejue/custom/hci-solution-hero-banner.svg'
import computeNetworkHeroBanner from '@/assets/jiejue/custom/compute-network-hero-banner.svg'
import paasSolutionHeroBanner from '@/assets/jiejue/custom/paas-solution-hero-banner.svg'

const route = useRoute()
const loaded = ref(false)
const activeTab = ref('background')
const isScrollingProgrammatically = ref(false)
const isUserScrolling = ref(false)
const visible = ref({
  background: false,
  challenges: false,
  scenarios: false,
  architecture: false,
  advantages: false,
  cases: false,
})

const key = computed(() => route.meta.solutionKey || '')
const content = computed(() => edgeInfrastructureSolutions[key.value] || null)

const architectureMap = {
  'ai-infrastructure': aiArch,
  'server-virtualization': serverArch,
  'edge-computing': edgeArch,
  'vm-container-fusion': fusionArch,
  'database-cloud': dbArch,
  'compute-network-infrastructure': netArch,
}

const architectureSrc = computed(() => architectureMap[key.value] || '')
const heroBannerMap = {
  'server-virtualization': serverVirtualizationHeroBanner,
  'ai-infrastructure': aiInfrastructureHeroBanner,
  'edge-computing': edgeComputingHeroBanner,
  'vm-container-fusion': hciSolutionHeroBanner,
  'compute-network-infrastructure': computeNetworkHeroBanner,
  'database-cloud': paasSolutionHeroBanner,
}
const heroBannerSrc = computed(() => heroBannerMap[key.value] || defaultHeroBanner)
const hasHomeHero = computed(() =>
  ['ai-infrastructure', 'edge-computing', 'vm-container-fusion', 'compute-network-infrastructure', 'database-cloud'].includes(key.value)
)
const tabs = [
  { id: 'background', label: '需求背景' },
  { id: 'challenges', label: '业务挑战' },
  { id: 'scenarios', label: '场景应用' },
  ...(SHOW_SOLUTION_DEPLOY_DIAGRAM ? [{ id: 'architecture', label: '部署示意图' }] : []),
  { id: 'advantages', label: '方案优势' },
  { id: 'cases', label: '典型案例' },
]

const cleanListPrefix = (value) => {
  if (!value || typeof value !== 'string') return value
  return value
    .replace(/^\s*\d+\s*[.、．]\s*/u, '')
    .replace(/^\s*[（(][一二三四五六七八九十]+[)）]\s*/u, '')
    .replace(/^\s*[一二三四五六七八九十]+\s*[、.．]\s*/u, '')
    .trim()
}

const scrollTo = (id) => {
  const el = document.getElementById(id)
  if (!el) return
  activeTab.value = id
  history.replaceState(null, '', `#${id}`)
  isScrollingProgrammatically.value = true
  el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  setTimeout(() => {
    isScrollingProgrammatically.value = false
  }, 1000)
}

let animationObserver = null
let navObserver = null
let userScrollTimeout = null
let handleUserScroll = null

onMounted(() => {
  activeTab.value = 'background'
  handleUserScroll = () => {
    if (isScrollingProgrammatically.value) return
    isUserScrolling.value = true
    if (userScrollTimeout) clearTimeout(userScrollTimeout)
    userScrollTimeout = setTimeout(() => {
      isUserScrolling.value = false
    }, 150)
  }
  window.addEventListener('scroll', handleUserScroll, { passive: true })

  animationObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && visible.value[entry.target.id] === false) {
        visible.value[entry.target.id] = true
      }
    })
  }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' })

  navObserver = new IntersectionObserver((entries) => {
    if (isScrollingProgrammatically.value || !isUserScrolling.value) return
    const visibleEntries = entries.filter((entry) => entry.isIntersecting)
    if (!visibleEntries.length) return
    let maxEntry = visibleEntries[0]
    for (const entry of visibleEntries) {
      if (entry.intersectionRatio > maxEntry.intersectionRatio) maxEntry = entry
    }
    activeTab.value = maxEntry.target.id
  }, {
    root: null,
    threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5],
    rootMargin: '-80px 0px -50% 0px',
  })

  ;['background', 'challenges', 'scenarios', 'architecture', 'advantages', 'cases'].forEach((id) => {
    const el = document.getElementById(id)
    if (el) {
      animationObserver.observe(el)
      navObserver.observe(el)
    }
  })
})

onBeforeUnmount(() => {
  if (animationObserver) animationObserver.disconnect()
  if (navObserver) navObserver.disconnect()
  if (userScrollTimeout) clearTimeout(userScrollTimeout)
  if (handleUserScroll) window.removeEventListener('scroll', handleUserScroll)
})
</script>

<style scoped>
@import '../../assets/css/solution-page.css';

.ai-platform-page { font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif; color: var(--color-text); }
.ai-platform-block { opacity: 0; transform: translateY(30px); transition: opacity 0.6s ease, transform 0.6s ease; }
.ai-platform-block.animate-in { opacity: 1; transform: translateY(0); }
.background-card { width: 100%; background: #fff; border-radius: 8px; padding: 36px 40px; border: 1px solid #e8e8e8; box-shadow: 0 2px 12px rgba(0,0,0,.08); box-sizing: border-box; }
.grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
.grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
.card { display: flex; align-items: flex-start; gap: 17px; background: #f5f7fa; border-radius: 6px; padding: 30px; min-height: 140px; box-sizing: border-box; }
.card-icon { flex-shrink: 0; width: 24px; height: 24px; }
.card-title { font-size: 18px; font-weight: 600; color: #000; margin-bottom: 8px; }
.card-desc { font-size: 14px; line-height: 1.7; color: #666; }
.challenges-description {
  margin: 0 0 20px;
  font-size: 16px;
  line-height: 1.9;
  color: #333;
  text-align: left;
}

.section-content-cases { text-align: left; }
.section-content-cases .case-content,
.section-content-cases .case-section,
.section-content-cases .case-text,
.section-content-cases .case-list { text-align: left; }

.case-content { max-width: 100%; margin: 0 auto; text-align: left; }

.scenarios {
  display: block;
  position: relative;
  width: 100%;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  overflow: visible;
}
.scenario-text {
  background: transparent;
  width: 100%;
  max-width: 100%;
  border-radius: 0;
  padding: 50px 40px 28px 40px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow: hidden;
  text-align: start;
}
.scenario-item { margin-bottom: 18px; }
.scenario-item .item-title {
  font-size: 16px;
  line-height: 16px;
  color: #333;
  margin-bottom: 8px;
  font-family: PingFang SC;
}
.scenario-item .item-desc { font-size: 14px; line-height: 1.8; color: #666; }
#advantages { background: linear-gradient(180deg, #f5f7fa 0%, #e0ecff 100%); max-width: unset; }
.adv-card { min-height: 200px; position: relative; background: #fff; border-radius: 4px; padding: 29px; display: grid; grid-template-columns: auto 1fr; grid-template-rows: auto auto; column-gap: 8px; row-gap: 10px; box-sizing: border-box; text-align: start; }
.adv-badge { display: block !important; position: static; grid-column: 1; grid-row: 1; width: 24px; height: 24px; border-radius: 4px; object-fit: contain; }
.adv-title { grid-column: 2; grid-row: 1; font-size: 18px; font-weight: 600; color: #333; margin: 0; align-self: start; text-align: left; }
.adv-desc { grid-column: 1 / -1; grid-row: 2; font-size: 14px; line-height: 1.6; color: #333; }
.case-content-blink { padding: 24px; border-radius: 8px; animation: case-blink 2.5s ease-in-out infinite; }
@keyframes case-blink { 0%,100% { opacity: 1; box-shadow: 0 0 0 rgba(6,85,159,0);} 50% { opacity: .88; box-shadow: 0 0 28px rgba(6,85,159,.25);} }
.case-section + .case-section { margin-top: 32px; }
.case-subtitle { font-size: 20px; font-weight: 600; color: #06559f; margin-bottom: 12px; }
.case-text, .case-list { font-size: 14px; line-height: 1.8; color: #333; }
.case-list { list-style: none; padding-left: 0; }
.architecture-image { width: 100%; max-width: 100%; border-radius: 8px; border: 1px solid #dbe3f0; display: block; margin: 0; }
@media (max-width: 1200px) { .grid-4 { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 768px) {
  .grid-2, .grid-4 { grid-template-columns: 1fr; }
  .scenario-text {
    border-radius: 4px;
    width: 100%;
    padding: 40px 30px;
    box-sizing: border-box;
  }
}
</style>
